import React from 'react'
import { Maximize2, Minimize2, X } from 'lucide-react'
import { Rnd } from 'react-rnd'
import { mergeClasses } from "../../helpers/styling"
import { SvgIcon, SvgIconType } from '../svg-icon'
import { SimpleTooltip } from './simple-tooltip'
import { PanelBody, PanelHeader, PanelHeaderActions, PanelHeaderTitle, PanelShell } from './panel-chrome'

interface FloatingPanelProps {
  children: React.ReactNode
  title: string
  icon?: SvgIconType
  onClose: () => void
  defaultPosition?: { x: number; y: number }
  defaultSize?: { width: number; height: number }
  minSize?: { width: number; height: number }
  extraHeaderIcons?: React.ReactNode
  /**
   * Offers the full-screen toggle, remembered per browser under this key. Off when absent: a panel opts in
   * with a key of its own, so two panels never share the preference.
   */
  maximizeStorageKey?: string
}

export function FloatingPanel({
                                children,
                                title,
                                icon,
                                onClose,
                                defaultPosition,
                                defaultSize = { width: 800, height: 600 },
                                minSize = { width: 500, height: 350 },
                                extraHeaderIcons,
                                maximizeStorageKey,
                              }: FloatingPanelProps) {
  /*
   * Panel state, not caller state: none of the panels that offer it should have to hold it. Remembered, so
   * someone who works with one full screen does not put it back there every time they open it.
   */
  const storageKey = maximizeStorageKey && `wistor:floating-panel:maximized:${maximizeStorageKey}`
  const [maximized, setMaximized] = React.useState(() => (storageKey ? readStoredFlag(storageKey) : false))

  const toggleMaximized = () => {
    if (!storageKey) return
    writeStoredFlag(storageKey, !maximized)
    setMaximized(!maximized)
  }

  /*
   * The panel's own geometry, tracked here rather than left to Rnd.
   *
   * Handing Rnd `size` and `position` only while maximized does not work:
   * receiving them once makes it adopt them, so withdrawing the props leaves the
   * panel sitting at full screen with nothing to restore to. Keeping the
   * dragged-to geometry ourselves is what makes restoring possible at all.
   */
  const [geometry, setGeometry] = React.useState(() => ({
    x: defaultPosition?.x ?? Math.max(0, window.innerWidth - defaultSize.width - 20),
    y: defaultPosition?.y ?? 60,
    width: defaultSize.width as number | string,
    height: defaultSize.height as number | string,
  }))

  return (
    <Rnd
      /* A fixed element's 100% is the viewport without its scrollbar, which 100vw is not; it follows the window too. */
      size={maximized ? { width: '100%', height: '100%' } : { width: geometry.width, height: geometry.height }}
      position={maximized ? { x: 0, y: 0 } : { x: geometry.x, y: geometry.y }}
      disableDragging={maximized}
      enableResizing={!maximized}
      onDragStop={(_event, data) => setGeometry((was) => ({ ...was, x: data.x, y: data.y }))}
      onResizeStop={(_event, _direction, ref, _delta, position) =>
        setGeometry({ x: position.x, y: position.y, width: ref.offsetWidth, height: ref.offsetHeight })
      }
      minWidth={minSize.width}
      minHeight={minSize.height}
      bounds="window"
      dragHandleClassName="drag-handle"
      style={{ position: 'fixed', zIndex: 100 }}
    >
      <PanelShell>
        <PanelHeader>
          <PanelHeaderTitle icon={icon} title={title} />
          <PanelHeaderActions>
            {extraHeaderIcons}
            {storageKey && (
              <FloatingPanelHeaderIcon
                title={maximized ? 'Restore' : 'Full screen'}
                icon={maximized ? Minimize2 : Maximize2}
                onClick={toggleMaximized}
              />
            )}
            <FloatingPanelHeaderIcon title="Close" icon={X} onClick={onClose} />
          </PanelHeaderActions>
        </PanelHeader>
        <PanelBody>
          {children}
        </PanelBody>
      </PanelShell>
    </Rnd>
  )
}

/*
 * Both wrapped, because localStorage throws rather than returns null when it is
 * unavailable — Safari's private mode, a storage quota, a blocked third-party
 * context. A remembered window size is never worth taking a panel down over.
 */
function readStoredFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === 'true'
  } catch {
    return false
  }
}

function writeStoredFlag(key: string, value: boolean) {
  try {
    window.localStorage.setItem(key, String(value))
  } catch {
    /* Preference not kept; the panel still works. */
  }
}

export function FloatingPanelHeaderIcon(props: {
  title: string
  icon: SvgIconType
  onClick: () => void
  disabled?: boolean
  iconSize?: number
}) {
  return (
    <SimpleTooltip tooltip={props.disabled ? null : props.title}>
      <button
        type="button"
        onClick={props.onClick}
        onMouseDown={e => e.stopPropagation()}
        disabled={props.disabled}
        className={mergeClasses(
          "rounded-full p-1 opacity-75 transition",
          props.disabled ? "opacity-50" : "hover:opacity-100 hover:bg-white hover:bg-opacity-25"
        )}
      >
        <SvgIcon icon={props.icon} size={props.iconSize ?? 14} />
      </button>
    </SimpleTooltip>
  )
}
